import { useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  Package,
  ArrowLeftRight,
  ChevronLeft,
  ChevronRight,
  MapPin,
} from "lucide-react";

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatQtd(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === 0) return "-";
  return parseInt(String(value)).toLocaleString("pt-BR");
}

export default function AddressMovements() {
  const { user, isLoading: authLoading } = useGoogleAuth();
  const [searchInput, setSearchInput] = useState("");
  const [endereco, setEndereco] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // Query para buscar saldo atual do endereço
  const { data: addressData, isLoading: addressLoading } = trpc.addressMovements.getByAddress.useQuery(
    { endereco },
    { enabled: !!endereco }
  );

  // Query para buscar histórico de movements do endereço
  const { data: historyData, isLoading: historyLoading } = trpc.addressMovements.getHistory.useQuery(
    { endereco, page, pageSize: PAGE_SIZE },
    { enabled: !!endereco }
  );

  // Query para listar endereços com saldo (autocomplete)
  const { data: addressList } = trpc.addressMovements.listWithStock.useQuery();

  function handleSearch() {
    if (!searchInput.trim()) {
      toast.error("Digite um endereço para buscar");
      return;
    }
    setEndereco(searchInput.trim().toUpperCase());
    setPage(1);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      handleSearch();
    }
  }

  function getOccupancyColor(percent: number): string {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 70) return "bg-yellow-500";
    return "bg-green-500";
  }

  function getMovementDirection(mov: any): { label: string; color: string } {
    if (mov.destino === endereco) {
      return { label: "ENTRADA", color: "bg-green-100 text-green-800" };
    }
    return { label: "SAÍDA", color: "bg-red-100 text-red-800" };
  }

  if (authLoading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ArrowLeftRight className="h-6 w-6 text-orange-500" />
        <h1 className="text-2xl font-bold">Movimentação por Endereços</h1>
      </div>

      {/* Busca */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="Digite o endereço (ex: A01-01-01)"
                className="h-10 pr-10"
                list="address-suggestions"
              />
              <datalist id="address-suggestions">
                {addressList?.map((a: any) => (
                  <option key={a.endereco} value={a.endereco} />
                ))}
              </datalist>
            </div>
            <Button onClick={handleSearch} className="h-10">
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultado: Saldo Atual */}
      {endereco && (
        <>
          {addressLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : !addressData ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Endereço "{endereco}" não encontrado.
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Card de resumo do endereço */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-muted-foreground">Endereço</span>
                    </div>
                    <p className="text-xl font-bold">{addressData.address.endereco}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-muted-foreground">Produtos</span>
                    </div>
                    <p className="text-xl font-bold">{addressData.produtos.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowLeftRight className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-muted-foreground">Ocupação</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${getOccupancyColor(addressData.percentualOcupado)}`}
                          style={{ width: `${Math.min(addressData.percentualOcupado, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{addressData.percentualOcupado.toFixed(1)}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {addressData.cubagemOcupada.toFixed(6)} / {addressData.cubagemTotal.toFixed(6)} m³
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Tabela de produtos no endereço */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Produtos no Endereço</CardTitle>
                </CardHeader>
                <CardContent>
                  {addressData.produtos.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">Nenhum produto neste endereço.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Código</th>
                            <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Descrição</th>
                            <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Quantidade</th>
                            <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Cubagem Unit.</th>
                            <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Cubagem Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {addressData.produtos.map((p: any, idx: number) => (
                            <tr key={idx} className="hover:bg-muted/30">
                              <td className="py-2 px-3 font-mono">{p.productCod}</td>
                              <td className="py-2 px-3 text-muted-foreground">{p.descricao || "-"}</td>
                              <td className="py-2 px-3 text-center">{formatQtd(p.quantidade)}</td>
                              <td className="py-2 px-3 text-center">{Number(p.cubagemUnit).toFixed(6)}</td>
                              <td className="py-2 px-3 text-center">{(Number(p.quantidade) * Number(p.cubagemUnit)).toFixed(6)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Histórico de Movimentações */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Histórico de Movimentações</CardTitle>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="text-center py-4 text-muted-foreground">Carregando histórico...</div>
                  ) : !historyData || historyData.rows.length === 0 ? (
                    <p className="text-center py-4 text-muted-foreground">Nenhuma movimentação concluída para este endereço.</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-center py-2 px-3 font-semibold text-muted-foreground">ID</th>
                              <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Direção</th>
                              <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Tipo</th>
                              <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Produto</th>
                              <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Qtd</th>
                              <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Origem</th>
                              <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Destino</th>
                              <th className="text-center py-2 px-3 font-semibold text-muted-foreground">Concluído</th>
                              <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Referência</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {historyData.rows.map((mov: any) => {
                              const direction = getMovementDirection(mov);
                              return (
                                <tr key={mov.id} className="hover:bg-muted/30">
                                  <td className="py-2 px-3 text-center font-mono">{mov.id}</td>
                                  <td className="py-2 px-3 text-center">
                                    <Badge className={direction.color}>{direction.label}</Badge>
                                  </td>
                                  <td className="py-2 px-3 text-center">{mov.tipo}</td>
                                  <td className="py-2 px-3 font-mono">{mov.produto}</td>
                                  <td className="py-2 px-3 text-center">{formatQtd(mov.quantidade)}</td>
                                  <td className="py-2 px-3">{mov.origem}</td>
                                  <td className="py-2 px-3">{mov.destino}</td>
                                  <td className="py-2 px-3 text-center">{formatDate(mov.concluidoEm)}</td>
                                  <td className="py-2 px-3 text-muted-foreground">{mov.referencia || mov.numeroPedido || "-"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Paginação */}
                      {historyData.totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4">
                          <span className="text-sm text-muted-foreground">
                            Página {page} de {historyData.totalPages} ({historyData.total} registros)
                          </span>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page <= 1}
                              onClick={() => setPage(p => p - 1)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page >= historyData.totalPages}
                              onClick={() => setPage(p => p + 1)}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
